import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { App } from './App.tsx'
import {
  createVgsCardFields,
  type CardFieldsAdapter
} from './lib/cardFields.ts'
import { readConfig, terminalIdFromPath } from './lib/config.ts'
import { createTerminalApi } from './lib/terminalApi.ts'
import {
  lookupTerminalOrigins,
  pageAllowsLocalHttp
} from './lib/terminalOrigins.ts'
import './index.css'

const rootEl = document.getElementById('root')
if (!rootEl) {
  throw new Error('#root element missing from index.html')
}

const config = readConfig(import.meta.env)
const allowLocalHttp = pageAllowsLocalHttp(
  window.location.protocol,
  window.location.hostname
)

function lookupOrigins(terminalId: string) {
  return lookupTerminalOrigins({
    apiBase: config.apiBase,
    terminalId,
    allowLocalHttp
  })
}

async function cardFieldsFactory(): Promise<() => CardFieldsAdapter> {
  // Vite replaces this env read at build time, so a production build drops the mock chunk.
  if (import.meta.env.VITE_CARD_FIELDS === 'mock') {
    const { createMockCardFields } = await import('./lib/mockCardFields.ts')
    return createMockCardFields
  }
  return () =>
    createVgsCardFields({
      vaultId: config.vgsVaultId,
      environment: config.vgsEnvironment
    })
}

const createCardFields = await cardFieldsFactory()
const terminalId = terminalIdFromPath(window.location.pathname)
const initialLookup = terminalId ? await lookupOrigins(terminalId) : undefined

createRoot(rootEl).render(
  <StrictMode>
    <App
      win={window}
      terminalId={terminalId}
      api={createTerminalApi({ baseUrl: config.apiBase })}
      lookupOrigins={lookupOrigins}
      initialLookup={initialLookup}
      createCardFields={createCardFields}
    />
  </StrictMode>
)
